import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  Squares2X2Icon,
  QuestionMarkCircleIcon,
  ScissorsIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import {
  buildWindows,
  camerasForMerge,
  gridArea,
  mergeInto,
  rectArea,
  rectCells,
  rectIsOneWindow,
  selectionBetween,
  splitAt,
  WALL_PRESETS,
  type CellRect,
  type WallMerge,
  type WallPreset,
  type WallWindow,
} from './wallLayout';
import { BTN_PRIMARY_SM, BTN_SECONDARY_SM } from '../common/ui';

/**
 * The Live Wall's merge editor — how an operator turns a plain grid into "one big window and
 * the rest small", on both products' walls.
 *
 * ─── THE GESTURE ───
 * DRAG across neighbouring windows to select a rectangle, then press Merge. That is the
 * spreadsheet gesture, which is the one people already have for "these cells become one".
 * A plain click is left exactly as it was (click an empty window → pick a camera), so nothing
 * an operator already knew stops working; a click only becomes a selection when the pointer
 * crosses into another window. Shift-click on a second window selects the rectangle between
 * the two, for anyone who finds dragging awkward (trackpads, large walls).
 *
 * ─── WHY SELECT-THEN-CONFIRM, NOT MERGE-ON-RELEASE ───
 * A merge can take cameras off the wall (only one camera fits in one window), and a drag is
 * easy to start by accident. So the release only SELECTS; a bar at the bottom of the wall
 * says what merging will do — "keeps Gate 1 · removes Lobby" — and the operator commits with
 * Merge (or Enter) or backs out with Cancel (or Esc). Nothing is saved until the layout's own
 * Save, so even a committed merge is one Split (or Cancel) away from undone.
 *
 * Pointer events with `elementFromPoint` rather than per-window enter/leave: a touch pointer
 * is implicitly captured by the element it went down on, so enter/leave never fire on a
 * tablet — hit-testing the point works for mouse, pen and touch alike.
 */

interface Slot { cameraId: string; cameraName: string; gridPosition: number }

interface Options<T extends Slot> {
  gridSize: string;
  merges: WallMerge[];
  setMerges: (next: WallMerge[]) => void;
  gridCameras: (T | null)[];
  setGridCameras: (next: (T | null)[]) => void;
  isEditing: boolean;
  /** A click (no drag) on a window — the page opens the camera picker for an empty one. */
  onWindowClick?: (w: WallWindow) => void;
}

export interface WallMergePending<T extends Slot> {
  rect: CellRect;
  kept: T | null;
  removed: T[];
}

export function useWallMerge<T extends Slot>({
  gridSize, merges, setMerges, gridCameras, setGridCameras, isEditing, onWindowClick,
}: Options<T>) {
  const windows = useMemo(() => buildWindows(gridSize, merges), [gridSize, merges]);
  const [selection, setSelection] = useState<CellRect | null>(null);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ start: WallWindow; moved: boolean } | null>(null);
  // The window a plain click last landed on — the first corner of a Shift-click selection.
  const lastClick = useRef<WallWindow | null>(null);

  // A selection belongs to one grid shape in one editing session.
  useEffect(() => {
    setSelection(null);
    setDragging(false);
    drag.current = null;
    lastClick.current = null;
  }, [gridSize, isEditing]);

  const byPosition = useMemo(() => new Map(windows.map((w) => [w.position, w])), [windows]);

  const windowFromElement = useCallback((el: Element | null): WallWindow | null => {
    const host = el?.closest?.('[data-wallpos]');
    if (!host) return null;
    return byPosition.get(Number(host.getAttribute('data-wallpos'))) || null;
  }, [byPosition]);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (!isEditing || e.button !== 0) return;
    const target = e.target as Element;
    // The window's own buttons (remove, split, a player's retry) keep their clicks.
    if (target.closest('button')) return;
    const w = windowFromElement(target);
    if (!w) return;

    // No preventDefault here: it would keep focus in the layout-name field, where Enter
    // means Save rather than Merge.
    if (e.shiftKey && lastClick.current) {
      const rect = selectionBetween(lastClick.current, w, gridSize, merges);
      setSelection(rectIsOneWindow(rect, windows) ? null : rect);
      drag.current = null;
      return;
    }
    drag.current = { start: w, moved: false };
    setSelection(null);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* capture is a nicety */ }
  }, [isEditing, windowFromElement, gridSize, merges, windows]);

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d) return;
    const w = windowFromElement(document.elementFromPoint(e.clientX, e.clientY));
    if (!w) return;
    if (w.position !== d.start.position) d.moved = true;
    if (!d.moved) return;
    setDragging(true);
    setSelection(selectionBetween(d.start, w, gridSize, merges));
  }, [windowFromElement, gridSize, merges]);

  const onPointerUp = useCallback(() => {
    const d = drag.current;
    drag.current = null;
    setDragging(false);
    if (!d) return;
    if (!d.moved) {
      lastClick.current = d.start;
      setSelection(null);
      onWindowClick?.(d.start);
      return;
    }
    setSelection((sel) => (sel && !rectIsOneWindow(sel, windows) ? sel : null));
  }, [onWindowClick, windows]);

  const onPointerCancel = useCallback(() => {
    drag.current = null;
    setDragging(false);
    setSelection(null);
  }, []);

  const pending: WallMergePending<T> | null = useMemo(() => {
    if (!selection || dragging || rectIsOneWindow(selection, windows)) return null;
    const { kept, removed } = camerasForMerge(gridCameras, selection, gridSize);
    return { rect: selection, kept, removed };
  }, [selection, dragging, windows, gridCameras, gridSize]);

  const commit = useCallback(() => {
    if (!pending) return;
    setGridCameras(camerasForMerge(gridCameras, pending.rect, gridSize).next);
    setMerges(mergeInto(merges, pending.rect, gridSize));
    setSelection(null);
  }, [pending, gridCameras, gridSize, merges, setGridCameras, setMerges]);

  const cancel = useCallback(() => setSelection(null), []);

  /** Break a merged window back into single cells. Its camera stays in the top-left one. */
  const split = useCallback((position: number) => {
    setMerges(splitAt(merges, position));
    setSelection(null);
  }, [merges, setMerges]);

  // Enter merges, Esc backs out — only while a selection is waiting on a decision, and never
  // while the operator is typing the layout's name.
  useEffect(() => {
    if (!pending) return undefined;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending, commit, cancel]);

  return {
    windows,
    selection,
    dragging,
    pending,
    commit,
    cancel,
    split,
    /** Spread on the CSS grid element. Inert outside editing. */
    gridProps: isEditing
      ? {
          onPointerDown,
          onPointerMove,
          onPointerUp,
          onPointerCancel,
          // No scroll or text selection hijacking the drag.
          style: { touchAction: 'none' } as CSSProperties,
          className: 'select-none',
        }
      : { style: {} as CSSProperties, className: '' },
  };
}

// ─── Pieces the pages drop into their walls ─────────────────────────────────

/** Brand tint without a Tailwind `/opacity` modifier — those compile to nothing on `var()` tokens. */
const BRAND_TINT = 'color-mix(in srgb, var(--brand) 16%, transparent)';

/**
 * The rectangle being selected, drawn as a grid item over the windows it covers. Pointer-inert,
 * so the drag keeps hit-testing the windows underneath it.
 */
export function WallSelectionOverlay({ selection, dragging }: { selection: CellRect | null; dragging: boolean }) {
  if (!selection) return null;
  const rows = selection.r1 - selection.r0 + 1;
  const cols = selection.c1 - selection.c0 + 1;
  return (
    <div
      aria-hidden
      className="pointer-events-none relative z-30 rounded-lg border-2 border-dashed border-brand"
      style={{ ...rectArea(selection), background: BRAND_TINT }}
    >
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-brand px-2 py-1 text-xs font-semibold text-white shadow">
        {rows} × {cols}{dragging ? ` · ${rectCells(selection)} cells` : ''}
      </span>
    </div>
  );
}

/**
 * What the selection will become, and the two ways out. Pinned to the bottom of the wall (the
 * page's wall container must be `relative`) rather than to the selection, so it is never
 * clipped by the wall's edge on a small selection in a corner.
 */
export function WallMergeBar<T extends Slot>({
  pending, onMerge, onCancel,
}: { pending: WallMergePending<T> | null; onMerge: () => void; onCancel: () => void }) {
  if (!pending) return null;
  const { rect, kept, removed } = pending;
  const rows = rect.r1 - rect.r0 + 1;
  const cols = rect.c1 - rect.c0 + 1;

  let effect: string;
  if (!kept) effect = 'Empty for now — click it afterwards to add a camera.';
  else if (!removed.length) effect = `${kept.cameraName} will fill the new window.`;
  else {
    const names = removed.slice(0, 2).map((c) => c.cameraName).join(', ');
    const more = removed.length > 2 ? ` and ${removed.length - 2} more` : '';
    effect = `Keeps ${kept.cameraName} · removes ${names}${more} (one camera per window).`;
  }

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-3 z-40 flex justify-center px-3">
      <div
        role="dialog"
        aria-label="Combine windows"
        className="pointer-events-auto flex max-w-xl flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-border bg-card px-3 py-2 shadow-2xl"
      >
        <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-brand text-white">
          <Squares2X2Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1 leading-tight">
          <p className="text-sm font-semibold text-foreground">Combine into one {rows} × {cols} window</p>
          <p className={`mt-0.5 text-xs ${removed.length ? 'text-amber-500' : 'text-muted-foreground'}`}>{effect}</p>
        </div>
        <div className="flex flex-none items-center gap-1.5">
          <button
            type="button"
            onClick={onMerge}
            className={BTN_PRIMARY_SM}
            title="Merge (Enter)"
          >
            <CheckIcon className="h-3.5 w-3.5" />
            Merge
          </button>
          <button
            type="button"
            onClick={onCancel}
            className={BTN_SECONDARY_SM}
            title="Cancel (Esc)"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/** Split a merged window — top-left, clear of the remove button a camera tile has top-right. */
export function WallSplitButton({ onSplit }: { onSplit: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onSplit(); }}
      className="absolute left-1.5 top-1.5 z-20 flex items-center gap-1 rounded-md bg-black/60 px-1.5 py-1 text-[11px] font-medium text-white transition-colors hover:bg-black/80"
      title="Split this window back into single cells"
      aria-label="Split window"
    >
      <ScissorsIcon className="h-3.5 w-3.5" />
      Split
    </button>
  );
}

/**
 * An empty window. The page's own empty slot used to be inline in both walls; it is shared now
 * because a merged window has to be placed and labelled the same way on both.
 */
export function WallEmptyWindow({
  window: w, isEditing, onSplit,
}: { window: WallWindow; isEditing: boolean; onSplit?: () => void }) {
  return (
    <div
      data-wallpos={w.position}
      style={gridArea(w)}
      className={`relative flex items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
        isEditing ? 'cursor-pointer border-border hover:border-brand' : 'border-border opacity-60'
      }`}
    >
      {isEditing && (
        <div className="flex flex-col items-center gap-1.5 text-muted-foreground">
          <PlusIcon className="h-6 w-6 sm:h-8 sm:w-8" />
          <span className="text-[10px] font-medium sm:text-xs">Add Camera</span>
          {w.merged && (
            <span className="text-[10px] text-muted-foreground">{w.rowSpan} × {w.colSpan} window</span>
          )}
        </div>
      )}
      {isEditing && w.merged && onSplit && <WallSplitButton onSplit={onSplit} />}
    </div>
  );
}

// ─── Guidance ───────────────────────────────────────────────────────────────

const GUIDE_KEY = 'trinetra.liveWall.mergeGuideSeen';

function readSeen(): boolean {
  try { return window.localStorage.getItem(GUIDE_KEY) === '1'; } catch { return false; }
}

/**
 * The three things an editor can do to a wall, said once in full and then folded to a one-line
 * reminder. Shown only while editing — a wall being watched has nothing to learn.
 */
export function WallEditGuide() {
  const [open, setOpen] = useState(() => !readSeen());
  const dismiss = () => {
    setOpen(false);
    try { window.localStorage.setItem(GUIDE_KEY, '1'); } catch { /* per-viewer nicety only */ }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mb-2 flex flex-shrink-0 items-center gap-1.5 self-start rounded-md px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <QuestionMarkCircleIcon className="h-3.5 w-3.5" />
        Drag across windows to combine them · how does this work?
      </button>
    );
  }

  const steps: { title: string; body: string; art: JSX.Element }[] = [
    {
      title: 'Add cameras',
      body: 'Click an empty window to pick the camera it shows.',
      art: <GuideArt cells={[1, 0, 0, 0]} />,
    },
    {
      title: 'Combine windows',
      body: 'Drag across neighbouring windows — or click one, then Shift-click another — and press Merge. Combined windows are always rectangles.',
      art: <GuideArt cells={[2, 2, 0, 0]} />,
    },
    {
      title: 'Split or start from a preset',
      body: 'Press Split on a combined window to break it up. The grid menu has ready-made shapes like “1 + 5”.',
      art: <GuideArt cells={[0, 0, 0, 0]} split />,
    },
  ];

  return (
    <div className="mb-2 flex flex-shrink-0 flex-wrap items-start gap-3 rounded-lg border border-border bg-card px-3 py-2">
      <ol className="grid min-w-0 flex-1 gap-3 sm:grid-cols-3">
        {steps.map((s, i) => (
          <li key={s.title} className="flex min-w-0 items-start gap-2.5">
            {s.art}
            <div className="min-w-0 leading-snug">
              <p className="text-xs font-semibold text-foreground">
                <span className="mr-1 text-brand">{i + 1}.</span>{s.title}
              </p>
              <p className="text-[11px] text-muted-foreground">{s.body}</p>
            </div>
          </li>
        ))}
      </ol>
      <button
        type="button"
        onClick={dismiss}
        className="flex flex-none items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-foreground hover:bg-muted"
        aria-label="Hide these tips"
      >
        <XMarkIcon className="h-3.5 w-3.5" />
        Got it
      </button>
    </div>
  );
}

/** A 2×2 thumbnail: 0 = empty cell, 1 = a camera, 2 = part of a merged window. */
function GuideArt({ cells, split }: { cells: number[]; split?: boolean }) {
  const merged = cells.filter((c) => c === 2).length;
  return (
    <div className="grid h-9 w-12 flex-none grid-cols-2 grid-rows-2 gap-0.5 rounded bg-muted p-0.5">
      {merged ? (
        <>
          <div className="col-span-2 rounded-sm bg-brand" />
          <div className="rounded-sm bg-background" />
          <div className="rounded-sm bg-background" />
        </>
      ) : (
        cells.map((c, i) => (
          <div
            key={i}
            className={`rounded-sm ${c === 1 ? 'bg-brand' : 'bg-background'} ${split ? 'outline outline-1 outline-dashed outline-brand' : ''}`}
          />
        ))
      )}
    </div>
  );
}

// ─── Grid menu ──────────────────────────────────────────────────────────────

/** A tiny picture of a shape, drawn from the same windows the wall itself would draw. */
export function WallShapeThumb({ gridSize, merges }: { gridSize: string; merges: WallMerge[] }) {
  const windows = buildWindows(gridSize, merges);
  const [rows, cols] = gridSize.split('x').map(Number);
  return (
    <div
      className="grid h-7 w-10 flex-none gap-px rounded-sm bg-muted p-px"
      style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` }}
    >
      {windows.map((w) => (
        <div key={w.position} className={`rounded-[1px] ${w.merged ? 'bg-brand' : 'bg-background'}`} style={gridArea(w)} />
      ))}
    </div>
  );
}

/**
 * The editing grid menu: plain sizes, then the presets. Replaces the size-only list both walls
 * had, so the ready-made shapes sit where the shape is already chosen.
 */
export function WallGridMenu({
  gridSize, merges, sizes, onSize, onPreset,
}: {
  gridSize: string;
  merges: WallMerge[];
  sizes: { size: string; total: number }[];
  onSize: (size: string) => void;
  onPreset: (preset: WallPreset) => void;
}) {
  const plainActive = merges.length === 0;
  const presetActive = (p: WallPreset) =>
    p.gridSize === gridSize
    && p.merges.length === merges.length
    && p.merges.every((m) => merges.some((o) => o.position === m.position && o.colSpan === m.colSpan && o.rowSpan === m.rowSpan));

  return (
    <div className="max-h-80 overflow-y-auto py-1" style={{ scrollbarWidth: 'thin' }}>
      <p className="px-3 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Grid</p>
      {sizes.map(({ size, total }) => (
        <button
          key={size}
          type="button"
          onClick={() => onSize(size)}
          className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-xs transition-colors sm:text-sm ${
            gridSize === size && plainActive ? 'bg-muted font-medium text-brand' : 'text-foreground hover:bg-muted'
          }`}
        >
          <WallShapeThumb gridSize={size} merges={[]} />
          <span className="flex-1">{size}</span>
          <span className="text-[10px] text-muted-foreground">{total} cams</span>
        </button>
      ))}

      <p className="border-t border-border px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Presets · one large window
      </p>
      {WALL_PRESETS.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onPreset(p)}
          title={p.hint}
          className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-xs transition-colors sm:text-sm ${
            presetActive(p) ? 'bg-muted font-medium text-brand' : 'text-foreground hover:bg-muted'
          }`}
        >
          <WallShapeThumb gridSize={p.gridSize} merges={p.merges} />
          <span className="flex-1">{p.label}</span>
          <span className="text-[10px] text-muted-foreground">{p.gridSize}</span>
        </button>
      ))}
      <p className="px-3 pb-1.5 pt-1 text-[10px] leading-snug text-muted-foreground">
        Cameras already on the wall move into the new windows in order — the first one takes the large window.
      </p>
    </div>
  );
}
